/**
 * Visualization tokens.
 *
 * Dark mode is a *selected* set of steps for the dark surface, not an automatic
 * inversion of the light values — an inverted hue lands outside the lightness
 * band the dark surface needs and loses contrast.
 *
 * Every series color was validated against its own surface before use
 * (lightness band, chroma floor, adjacent-pair CVD separation, contrast).
 * Re-run the validator if any of them changes — including a re-ordering.
 *
 * Last run, six categorical slots: all checks pass in both modes. Worst
 * adjacent CVD ΔE 9.1 light / 8.4 dark (>= 8 target). Light mode carries a
 * contrast warning — aqua, yellow and magenta sit below 3:1 on the light
 * surface — which obligates relief: every chart here ships visible labels and
 * a table view, so no value is reachable by hue alone.
 *
 * Text never wears the series color: values, labels and axis ticks stay in ink
 * tokens, and the colored mark beside them carries identity.
 */

export interface VizTokens {
  surface: string;
  /** Categorical slot 1 — the single-series color. */
  series1: string;
  series1Hover: string;
  /**
   * Categorical slots 1–6 in fixed order, assigned by position and never
   * cycled. The *order* is the colorblind-safety mechanism, not cosmetic: it
   * was validated as a sequence, so re-ordering it invalidates the result.
   *
   * A seventh category is never a generated hue — `PieChart` folds the tail
   * into an "Other" bucket in `otherFill` instead.
   */
  categorical: string[];
  /** De-emphasis gray for a folded "Other" bucket. Not a categorical slot. */
  otherFill: string;
  textPrimary: string;
  textSecondary: string;
  /** Axis ticks and labels. */
  muted: string;
  gridline: string;
  axis: string;
  tooltipBg: string;
  tooltipBorder: string;
}

const light: VizTokens = {
  surface: "#fcfcfb",
  series1: "#2a78d6",
  series1Hover: "#256abf",
  categorical: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300"],
  otherFill: "#898781",
  textPrimary: "#0b0b0b",
  textSecondary: "#52514e",
  muted: "#898781",
  gridline: "#e1e0d9",
  axis: "#c3c2b7",
  tooltipBg: "#ffffff",
  tooltipBorder: "rgba(11,11,11,0.10)",
};

const dark: VizTokens = {
  surface: "#1a1a19",
  series1: "#3987e5",
  series1Hover: "#5598e7",
  categorical: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300"],
  otherFill: "#898781",
  textPrimary: "#ffffff",
  textSecondary: "#c3c2b7",
  muted: "#898781",
  gridline: "#2c2c2a",
  axis: "#383835",
  tooltipBg: "#242423",
  tooltipBorder: "rgba(255,255,255,0.10)",
};

export function vizTokens(scheme: "light" | "dark"): VizTokens {
  return scheme === "dark" ? dark : light;
}

/**
 * Mantine's default palette, shade 6 (light) / shade 4 (dark) — the same
 * shades Mantine itself uses for a filled surface in each mode.
 *
 * Backs bar-chart threshold coloring (config.ColorThreshold): a severity flag,
 * not a categorical identity, so it does not need the CVD-adjacency
 * validation `categorical` above carries — red-for-danger reads the same
 * regardless of what is next to it.
 */
const MANTINE_SHADE: Record<string, { light: string; dark: string }> = {
  blue: { light: "#228be6", dark: "#74c0fc" },
  cyan: { light: "#15aabf", dark: "#66d9e8" },
  grape: { light: "#be4bdb", dark: "#e599f7" },
  gray: { light: "#868e96", dark: "#dee2e6" },
  green: { light: "#40c057", dark: "#8ce99a" },
  indigo: { light: "#4c6ef5", dark: "#91a7ff" },
  lime: { light: "#82c91e", dark: "#c0eb75" },
  orange: { light: "#fd7e14", dark: "#ffa94d" },
  pink: { light: "#e64980", dark: "#faa2c1" },
  red: { light: "#fa5252", dark: "#ff8787" },
  teal: { light: "#12b886", dark: "#63e6be" },
  violet: { light: "#7950f2", dark: "#b197fc" },
  yellow: { light: "#fab005", dark: "#ffe066" },
};

/** Resolves a Mantine color name to a hex value for canvas rendering. */
export function mantineColor(name: string, scheme: "light" | "dark"): string {
  return MANTINE_SHADE[name]?.[scheme] ?? vizTokens(scheme).series1;
}

// Matches theme.ts's fontFamily. Kept separate rather than imported from
// there: ECharts renders to canvas, so it cannot inherit CSS font-family the
// way the rest of the app does — this has to be told explicitly every time,
// which is the only reason the two lists exist twice.
export const VIZ_FONT =
  '"Open Sans", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
